/**
 * The service map's presentation model, derived from the client-side
 * topology projection (`topology.ts`) and the lifecycle feed.
 *
 * Everything here is a pure function over plain values, so the rules that are
 * worth arguing about — grouping order, dev↔stage pairing, what counts as
 * live — are tested directly instead of through markup.
 *
 * What this deliberately does NOT do: infer state the feeds do not carry.
 * `transient` is `projectTopology`'s own answer, and a running tool is
 * attributed to the map rather than to a row because `ZeropsRecentTool` has
 * no hostname. It also no longer carries `mounted`, `adoptionState`, or
 * `doorbellConnected` — those were `zcp studio topology`-only facts; liveness
 * is now `useProjectTopology`'s own signal, composed by the caller rather than
 * by this function.
 *
 * ## Row order
 *
 * Sections come first (`GROUP_ORDER`: Runtimes, Data, Infrastructure), each
 * one fixed and never derived. A dev pool is a runtime kind here, never
 * infrastructure: it is where a part of the app is developed. Rows inside a
 * section are a total order, so two reads of the same topology — whatever
 * order the backend happened to return services in — always render
 * identically:
 *
 * 1. In Runtimes, the app's parts lead, by name — each a pool or a dev with
 *    its preview folded in — then every other runtime, then a holder at rest.
 *    In Infrastructure, the control plane (`isZcpServiceType`) leads — it is
 *    not a service the user created.
 * 2. Then hostname, via `compareZeropsHostnames` (locale-aware,
 *    case-insensitive, numeric-aware: `db` < `db2` < `db10`).
 * 3. Tiebreak on `serviceId`, so two rows can never compare equal.
 *
 * A folded stage row carries no position of its own — it is nested under
 * its dev partner and follows it automatically.
 */
import type {
  ZeropsDevServer,
  ZeropsLease,
  ZeropsLifecycle,
  ZeropsStandIn,
  ZeropsStateEnvelope,
} from "@t3tools/contracts";

import type { ZeropsStatPair } from "./api.ts";
import { threadLeases, type CheckoutLease } from "./gitTab.ts";
import type { Known } from "./knowledge/index.ts";
import { serviceDashboardUrl, zeropsProjectUrl } from "./gui.ts";
import { isPoolServiceType } from "./infrastructureType.ts";
import { compareZeropsHostnames } from "./listingOrder.ts";
import { poolWorkspaces, type LeaseChat, type PoolWorkspace } from "./poolWorkspaces.logic.ts";
import { poolPairsIn, servicePairs, type PoolPair } from "./servicePairs.ts";
import { isZcpServiceType } from "./topology.ts";
import type {
  ZeropsScalingRange,
  ZeropsServiceRoute,
  ZeropsServiceScaling,
  ZeropsServiceUsage,
  ZeropsTopologyGroup,
  ZeropsTopologyService,
  ZeropsTopologyView,
  ZeropsUsageSample,
} from "./topology.ts";

export type { LeaseChat, PoolWorkspace } from "./poolWorkspaces.logic.ts";

/** Reading order: what the user builds, what it stores, what runs it. */
const GROUP_ORDER: ReadonlyArray<{ group: ZeropsTopologyGroup; title: string }> = [
  { group: "runtimes", title: "Runtimes" },
  { group: "data", title: "Data" },
  { group: "infrastructure", title: "Infrastructure" },
];

/**
 * `"<name> (<projectId>)"`, the shape zcp renders a production launch as
 * (`internal/workflow/compute_envelope.go`, `prodLaunchRefsRender`).
 */
const PRODUCTION_LAUNCH = /^(.*?)\s*\(([^()\s]+)\)$/u;

export interface ZeropsProductionLink {
  readonly label: string;
  readonly projectId?: string;
  readonly url?: string;
}

/** One chip under a service's name: a short phrase, optionally dated or linked. */
export interface ZeropsServiceFact {
  readonly id: "hostname" | "type" | "deploy";
  readonly label: string;
  readonly url?: string;
  /** The instant the fact is dated at; the client says how long ago that was. */
  readonly at?: string;
}

/**
 * One figure of a service's live strip — what the Zerops dashboard shows as
 * "1 container · Cores · RAM · Disk". `fraction` is used over limit, for the
 * thin bar under the figure; absent where there is nothing to fill.
 */
export interface ZeropsServiceMetric {
  readonly id: "containers" | "cores" | "memory" | "disk";
  readonly label: string;
  /** The allocation, as the dashboard prints it; absent while the service holds no container. */
  readonly value?: string;
  /** What is in use of it; absent on the container count. */
  readonly used?: string;
  readonly unit?: string;
  readonly fraction?: number;
  /**
   * The autoscaling envelope the figure moves in — `1 – 3`, or `stays at 1`
   * where the two ends meet — in the row's unit; absent where the platform
   * states no range.
   */
  readonly range?: string;
  /** On the cores row: `Shared` or `Dedicated`. */
  readonly cpuMode?: string;
}

/** One point of a card's graph: the allocation and what was used of it, at an hour. */
export interface ZeropsTrendPoint {
  readonly at: string;
  readonly used: number;
  readonly limit: number;
}

/** The three graphs under a card's figures, oldest point first. */
export interface ZeropsServiceTrends {
  readonly cores: ReadonlyArray<ZeropsTrendPoint>;
  readonly memory: ReadonlyArray<ZeropsTrendPoint>;
  readonly disk: ReadonlyArray<ZeropsTrendPoint>;
}

/**
 * What a row is to the app: one of its parts (a dev pool or a dev service, its preview folded in),
 * a holder — a dev zcp keeps only for the repository while the parts run on their own pools — or
 * any other service.
 */
export type ZeropsServiceRowKind = "part" | "holder" | "service";

export interface ZeropsServiceRow {
  /** Infrastructure also contains platform services that do not host a Mate. */
  readonly isControlPlane: boolean;
  readonly kind: ZeropsServiceRowKind;
  /** A holder's line in words: why its card is at rest. */
  readonly restNote?: string;
  /**
   * A dev pool's workspaces, one per preview zcp read on a container it leases for the Mate: a link
   * where it answers, else why not (`poolWorkspaces`). Absent on every row but a pool's.
   */
  readonly workspaces?: ReadonlyArray<PoolWorkspace>;
  readonly service: ZeropsTopologyService;
  readonly tone: ZeropsServiceTone;
  /** The platform's status token as a word: `READY_TO_DEPLOY` reads as `Ready to deploy`. */
  readonly statusLabel: string;
  /** Whether the card says `statusLabel` beside its dot (`serviceStatusNeedsWord`). */
  readonly statusNeedsWord: boolean;
  /** `type` without its OS prefix: `ubuntu/nodejs@22` reads as `nodejs@22`. */
  readonly typeLabel: string;
  /**
   * What the service is, as the card's name line says it beside the port:
   * the platform's name and the major version, `Node.js 22`; the raw type
   * where the platform gave no name. Absent on the control plane, whose
   * name already says what it is.
   */
  readonly typeShort?: string;
  /**
   * The row's name: the service's hostname, or the glossary word for the control plane.
   */
  readonly title: string;
  /** The service's declared ports after its name, `:80` or `:80, :443`. */
  readonly portLabel?: string;
  /**
   * The one line under the name, in order: the hostname where the title is
   * not it (the control plane), then what the service is, then how its
   * running version got there. A client joins the segments.
   */
  readonly meta: ReadonlyArray<ZeropsServiceFact>;
  /** The service's own page in the Zerops dashboard. */
  readonly dashboardUrl: string;
  /** The live figures; empty until the first current-stats read, or for a service holding no container. */
  readonly metrics: ReadonlyArray<ZeropsServiceMetric>;
  /** Cores, RAM and disk over the last day; absent until the history read answers. */
  readonly trends?: ZeropsServiceTrends;
  /** The paired stage service, folded into its dev row. */
  readonly stage?: ZeropsTopologyService;
  readonly stageTone?: ZeropsServiceTone;
  readonly stageStatusLabel?: string;
  /**
   * The stage half's own resources. It is a separate container with its own
   * allocation and load, and without these it rendered like a service holding
   * nothing — the card's other one-line state.
   */
  readonly stageStatusNeedsWord?: boolean;
  readonly stageMetrics?: ReadonlyArray<ZeropsServiceMetric>;
  readonly stageTrends?: ZeropsServiceTrends;
  readonly stagePortLabel?: string;
  /** The service's public routes as its card offers them (`zeropsRouteChoices`). */
  readonly routes: ReadonlyArray<ZeropsRouteChoice>;
  /** Too many for a button each: the main route's button and one *Open* menu (`zeropsRoutesFolded`). */
  readonly routesFolded: boolean;
  readonly stageRoutes?: ReadonlyArray<ZeropsRouteChoice>;
  readonly stageRoutesFolded?: boolean;
  /** Production projects this service feeds, from the lifecycle envelope. */
  readonly production: ReadonlyArray<ZeropsProductionLink>;
  /**
   * The dev servers the lifecycle envelope last read on this service, each with
   * its `readAt`. Absent until a call has read them: not read, never none running.
   */
  readonly devServers?: ReadonlyArray<ZeropsDevServer>;
}

export interface ZeropsServiceMapGroup {
  readonly group: ZeropsTopologyGroup;
  readonly title: string;
  readonly rows: ReadonlyArray<ZeropsServiceRow>;
}

export interface ZeropsServiceMapView {
  readonly groups: ReadonlyArray<ZeropsServiceMapGroup>;
  readonly project: ZeropsTopologyView["project"];
  readonly isEmpty: boolean;
  readonly warnings: ReadonlyArray<string>;
  /** Whether a usage read has answered — until then a client reserves the strip's space. */
  readonly usageRead: boolean;
  /**
   * A phrase for the Zerops operation running right now, if any — the
   * caller's own reading of `ZeropsThreadModel.running` (passed in as
   * `runningTool`, see `buildZeropsServiceMap`), never derived here.
   *
   * The topology projection carries no live process state of its own beyond
   * `transient`, so this is the only signal that something is in flight. It
   * belongs to the map rather than to a row: the model records an operation,
   * never a hostname, and attributing it to a service would be a guess
   * dressed as a fact.
   */
  readonly runningTool?: string;
}

/** `ubuntu/nodejs@22` → `nodejs@22`; a type with no OS prefix is unchanged. */
export function zeropsTypeLabel(type: string): string {
  const slash = type.indexOf("/");
  return slash < 0 ? type : type.slice(slash + 1);
}

/**
 * A running, settled status is chrome; anything else is the interesting
 * case. Failure words are matched loosely on purpose — the platform's
 * vocabulary grows (`REPAIR_FAILED`, `CONTAINER_FAILED`, `ACTION_FAILED`)
 * and a status this build has not seen should still read as bad news rather
 * than as normal. `muted` is settled but not running: a stopped service, or
 * a runtime that has nothing deployed yet — never a green dot.
 */
export type ZeropsServiceTone = "error" | "warning" | "outline" | "muted";

const NOT_RUNNING_STATUSES: ReadonlySet<string> = new Set([
  "STOPPED",
  "READY_TO_DEPLOY",
  "DELETED",
]);

const SERVICE_STATUS_PREFIX = "SERVICE_";

export function serviceStatusTone(service: ZeropsTopologyService): ZeropsServiceTone {
  if (/FAIL/u.test(service.status)) {
    return "error";
  }
  if (service.transient) {
    return "warning";
  }
  const normalized = service.status.startsWith(SERVICE_STATUS_PREFIX)
    ? service.status.slice(SERVICE_STATUS_PREFIX.length)
    : service.status;
  return NOT_RUNNING_STATUSES.has(normalized) ? "muted" : "outline";
}

/**
 * Whether a service's card says its state in words. A running service leaves it to its green
 * dot (the word stays its accessible name); a stopped, failed, busy or unfamiliar one says it,
 * since that state needs saying.
 */
export function serviceStatusNeedsWord(service: ZeropsTopologyService): boolean {
  const normalized = service.status.startsWith(SERVICE_STATUS_PREFIX)
    ? service.status.slice(SERVICE_STATUS_PREFIX.length)
    : service.status;
  return !(serviceStatusTone(service) === "outline" && normalized === "ACTIVE");
}

const withKey = <K extends string, V>(key: K, value: V | undefined): Partial<Record<K, V>> =>
  value === undefined ? {} : ({ [key]: value } as Record<K, V>);

/** `READY_TO_DEPLOY` → `Ready to deploy`; a token the client has never seen still reads as words. */
export function zeropsStatusWord(status: string): string {
  const words = status.trim().replaceAll("_", " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The glossary word for the zcp container (`design-system.md` §2). */
const CONTROL_PLANE_TITLE = "Zerops Control Plane";

/** A pool's kind, where a dev service says what it runs. */
const POOL_TYPE_SHORT = "dev pool";

const HOLDER_NOTE = "Holds the code; nothing runs here";

/** Where in Runtimes a row sits: the parts, every other runtime, then a holder at rest. */
const KIND_ORDER: Readonly<Record<ZeropsServiceRowKind, number>> = {
  part: 0,
  service: 1,
  holder: 2,
};

/** `:80`, or `:80, :443` — the ports the service declares, after its name. */
/**
 * Who serves a dev service's crew ports (PRD §5.7), by hostname: each crew
 * port's crewmate by name, `null` for a crew port nobody holds. A port not in
 * it is the service's own.
 */
export type ZeropsCrewPortOwners = ReadonlyMap<string, ReadonlyMap<number, string | null>>;

/** More routes than this fold into one *Open* menu beside the main route's button. */
const ROUTE_MENU_AFTER = 2;

/** Whether a card folds its routes into one *Open* menu: more than it has room for as buttons. */
export const zeropsRoutesFolded = (routes: ReadonlyArray<ZeropsRouteChoice>): boolean =>
  routes.length > ROUTE_MENU_AFTER;

/** A service's ports in the card's order: its own first, then its crew ports, each ascending. */
function orderedPorts(
  ports: ReadonlyArray<number>,
  crew: ReadonlyMap<number, string | null> | undefined,
): ReadonlyArray<number> {
  const isCrew = (port: number) => crew?.has(port) === true;
  return [...ports].sort(
    (left, right) => Number(isCrew(left)) - Number(isCrew(right)) || left - right,
  );
}

export function zeropsPortLabel(
  service: ZeropsTopologyService,
  crew?: ReadonlyMap<number, string | null>,
): string | undefined {
  if (service.ports.length === 0) return undefined;
  return orderedPorts(
    service.ports.map((port) => port.port),
    crew,
  )
    .map((port) => `:${port}`)
    .join(", ");
}

/** One public route as the card offers it: its port and who it serves. */
export interface ZeropsRouteChoice {
  readonly route: ZeropsServiceRoute;
  /** `:3000 · the service's own`, `:3001 · Backend's app`, or the port alone. */
  readonly label: string;
}

/**
 * A service's public routes in the card's order — the service's own first
 * (the main route), then its crew ports — each named by its port and who it
 * serves: the service's own, a crewmate's app, or, for a crew port nobody
 * holds, the port alone.
 */
export function zeropsRouteChoices(
  service: ZeropsTopologyService,
  crew?: ReadonlyMap<number, string | null>,
): ReadonlyArray<ZeropsRouteChoice> {
  const byPort = new Map(service.routes.map((route) => [route.port, route]));
  const ports = orderedPorts(
    service.routes.map((route) => route.port),
    crew,
  );
  return ports.flatMap((port, index): ReadonlyArray<ZeropsRouteChoice> => {
    const route = byPort.get(port);
    if (route === undefined) return [];
    const owner = crew?.get(port);
    const label =
      owner !== undefined
        ? owner === null
          ? `:${port}`
          : `:${port} · ${owner}'s app`
        : index === 0
          ? `:${port} · the service's own`
          : `:${port}`;
    return [{ route, label }];
  });
}

/**
 * A figure the way the dashboard prints it: whole numbers stay whole, the
 * rest keep two decimals with trailing zeros dropped (`2.625` → `2.63`,
 * `0.5` → `0.5`).
 */
export function formatAmount(value: number): string {
  return Number(value.toFixed(2)).toString();
}

const EN_DASH = "\u2013";

/** `1 – 3`, or `stays at 1` where nothing scales. */
export function formatRange(range: ZeropsScalingRange): string {
  return range.min === range.max
    ? `stays at ${formatAmount(range.min)}`
    : `${formatAmount(range.min)} ${EN_DASH} ${formatAmount(range.max)}`;
}

const withRange = (range: ZeropsScalingRange | undefined) =>
  range === undefined ? {} : { range: formatRange(range) };

const CPU_MODE_WORDS: Record<string, string> = { SHARED: "Shared", DEDICATED: "Dedicated" };

const withCpuMode = (mode: string | undefined) =>
  mode === undefined ? {} : { cpuMode: CPU_MODE_WORDS[mode] ?? mode };

/**
 * The name line's word for what a service is: the platform's name and the
 * major version — `Node.js 22`, `PostgreSQL 16`, `Valkey 7.2` — or the bare
 * type where the platform gave no name.
 */
export function zeropsTypeShort(service: Pick<ZeropsTopologyService, "type" | "typeName">): string {
  const label = zeropsTypeLabel(service.type);
  if (service.typeName === undefined) return label;
  const at = label.lastIndexOf("@");
  return at < 0 ? service.typeName : `${service.typeName} ${label.slice(at + 1)}`;
}

const fractionOf = (used: number, limit: number): number | undefined =>
  limit > 0 ? Math.min(1, Math.max(0, used / limit)) : undefined;

const withFraction = (used: number, limit: number) => {
  const fraction = fractionOf(used, limit);
  return fraction === undefined ? {} : { fraction };
};

/**
 * The live strip from a service's usage: the containers it runs, and the
 * cores, RAM and disk they hold. The figure is the allocation (`limit`), as
 * the dashboard shows it; how much of it is in use rides as `fraction`.
 */
export function zeropsServiceMetrics(
  usage: ZeropsServiceUsage | undefined,
  scaling?: ZeropsServiceScaling,
): ReadonlyArray<ZeropsServiceMetric> {
  if (usage === undefined && scaling === undefined) return [];
  const held = (pair: ZeropsStatPair | undefined) =>
    pair === undefined
      ? {}
      : {
          value: formatAmount(pair.limit),
          used: formatAmount(pair.used),
          ...withFraction(pair.used, pair.limit),
        };
  return [
    {
      id: "containers",
      label: usage?.containers === 1 ? "container" : "containers",
      ...(usage === undefined ? {} : { value: String(usage.containers) }),
      ...withRange(scaling?.containers),
    },
    {
      id: "cores",
      label: "Cores",
      ...held(usage?.cores),
      ...withRange(scaling?.cores),
      ...withCpuMode(scaling?.cpuMode),
    },
    {
      id: "memory",
      label: "RAM",
      ...held(usage?.memoryGb),
      unit: "GB",
      ...withRange(scaling?.memoryGb),
    },
    {
      id: "disk",
      label: "Disk",
      ...held(usage?.diskGb),
      unit: "GB",
      ...withRange(scaling?.diskGb),
    },
  ];
}

/** The graphs' series: each resource held and used, hour by hour; nothing until the history answers. */
export function zeropsUsageTrends(
  history: ReadonlyArray<ZeropsUsageSample> | undefined,
): ZeropsServiceTrends | undefined {
  if (history === undefined) return undefined;
  const series = (pick: (sample: ZeropsUsageSample) => ZeropsStatPair) =>
    history.map((sample) => ({
      at: sample.at,
      used: pick(sample).used,
      limit: pick(sample).limit,
    }));
  return {
    cores: series((sample) => sample.cores),
    memory: series((sample) => sample.memoryGb),
    disk: series((sample) => sample.diskGb),
  };
}

const DEPLOY_SOURCE_WORDS: Readonly<Record<string, string>> = {
  CLI: "Deployed from CLI",
  GIT: "Deployed from Git",
  GITHUB: "Deployed from GitHub",
  GITLAB: "Deployed from GitLab",
  GUI: "Uploaded in the GUI",
};

const SHORT_COMMIT_LENGTH = 7;

/**
 * The line under the name: what the service is exactly (falling back to its
 * type-version when the platform gave no display name), and how its running
 * version got there. The type is skipped where it would only repeat the
 * row's title — the control plane is already named by its type.
 */
export function zeropsServiceFacts(
  service: ZeropsTopologyService,
  title: string = service.hostname,
): ReadonlyArray<ZeropsServiceFact> {
  const facts: Array<ZeropsServiceFact> = [];
  if (service.typeName === undefined) {
    facts.push({ id: "type", label: zeropsTypeLabel(service.type) });
  } else if (service.typeName !== title) {
    facts.push({
      id: "type",
      label:
        service.version === undefined ? service.typeName : `${service.typeName} ${service.version}`,
    });
  }
  const deploy = service.deploy;
  if (deploy !== undefined) {
    const verb = DEPLOY_SOURCE_WORDS[deploy.source] ?? "Deployed";
    const ref =
      deploy.branch !== undefined
        ? deploy.commit === undefined
          ? deploy.branch
          : `${deploy.branch}@${deploy.commit.slice(0, SHORT_COMMIT_LENGTH)}`
        : (deploy.tag ?? deploy.name);
    facts.push({
      id: "deploy",
      label: ref === undefined ? verb : `${verb} · ${ref}`,
      ...(deploy.activatedAt === undefined ? {} : { at: deploy.activatedAt }),
    });
  }
  return facts;
}

export function parseZeropsProductionLaunch(entry: string): ZeropsProductionLink {
  const match = PRODUCTION_LAUNCH.exec(entry.trim());
  const label = match?.[1]?.trim();
  const projectId = match?.[2];
  return label === undefined || label.length === 0 || projectId === undefined
    ? { label: entry.trim() }
    : { label, projectId, url: zeropsProjectUrl(projectId) };
}

/**
 * The production links a known lifecycle names for the service, stale or not. A lifecycle not
 * known yet names none: the map only lists the links it holds and never says a service feeds no
 * production, so an unread feed reads as nothing yet, not as none.
 */
const productionOf = (
  hostname: string,
  lifecycle: Known<ZeropsLifecycle> | undefined,
): ReadonlyArray<ZeropsProductionLink> => {
  if (lifecycle?.state !== "known") return [];
  const snapshot = lifecycle.value.envelope?.services.find((entry) => entry.hostname === hostname);
  return (snapshot?.feedsProduction ?? []).map(parseZeropsProductionLaunch);
};

/** What zcp holds a dev service in place for, as its snapshot says; none while the lifecycle is not known. */
export const standInOf = (
  hostname: string,
  lifecycle: Known<ZeropsLifecycle> | undefined,
): ZeropsStandIn | undefined =>
  lifecycle?.state === "known"
    ? lifecycle.value.envelope?.services.find((entry) => entry.hostname === hostname)?.standIn
    : undefined;

/**
 * The services a known lifecycle's envelope marks as a repository's stand-in (`standIn`), by
 * hostname; none while it is not known.
 */
export const standInsOf = (lifecycle: Known<ZeropsLifecycle> | undefined): ReadonlyArray<string> =>
  lifecycle?.state === "known"
    ? (lifecycle.value.envelope?.services ?? []).flatMap((entry) =>
        entry.standIn === undefined ? [] : [entry.hostname],
      )
    : [];

/** The dev servers zcp last read on a service; none while the lifecycle is not known. */
export const devServersOf = (
  hostname: string,
  lifecycle: Known<ZeropsLifecycle> | undefined,
): ReadonlyArray<ZeropsDevServer> | undefined =>
  lifecycle?.state === "known"
    ? lifecycle.value.envelope?.services.find((entry) => entry.hostname === hostname)?.devServers
    : undefined;

/**
 * The dev servers zcp last read on a lease (`leases[].devServers`, by alias), never a service's of
 * its name; none while the lifecycle is not known or nothing read them.
 */
export const leaseDevServersOf = (
  alias: string,
  lifecycle: Known<ZeropsLifecycle> | undefined,
): ReadonlyArray<ZeropsDevServer> | undefined =>
  lifecycle?.state === "known" ? leaseDevServersIn(alias, lifecycle.value.envelope) : undefined;

/**
 * The Mate's leases as one of its conversations' lifecycles last read them (`leases[]` on the
 * lifecycle's envelope); `undefined` while it is not read, or before that conversation made a
 * Zerops call: not read, never none.
 */
export const lifecycleLeases = (
  lifecycle: Known<ZeropsLifecycle> | undefined,
): ReadonlyArray<ZeropsLease> | undefined =>
  lifecycle?.state === "known" ? lifecycle.value.envelope?.leases : undefined;

/**
 * The leases a thread bound to `lease` reads (`threadLeases`): every lease of its repository and
 * branch its lifecycle last read zcp holding, its own first; the bound one alone while the
 * lifecycle is not known.
 */
export const threadLeasesOf = (
  lease: CheckoutLease | null | undefined,
  lifecycle: Known<ZeropsLifecycle> | undefined,
): ReadonlyArray<CheckoutLease> =>
  threadLeases(lease, lifecycle?.state === "known" ? lifecycle.value.envelope?.leases : undefined);

/**
 * The pairs a known lifecycle's envelope keys by a dev pool (`poolPairsIn`), stale or not; none
 * while it is not known.
 */
export const poolPairsOf = (
  lifecycle: Known<ZeropsLifecycle> | undefined,
): ReadonlyArray<PoolPair> =>
  lifecycle?.state === "known" ? poolPairsIn(lifecycle.value.envelope?.services ?? []) : [];

/** The dev servers one envelope read on a lease, by alias; none when it did not read them. */
export const leaseDevServersIn = (
  alias: string,
  envelope: Pick<ZeropsStateEnvelope, "leases"> | undefined,
): ReadonlyArray<ZeropsDevServer> | undefined =>
  envelope?.leases?.find((lease) => lease.alias === alias)?.devServers;

/**
 * The map to render, or undefined when there is nothing to render at all —
 * `useProjectTopology` has not produced a view yet (no session, no resolved
 * project, or the first read still in flight). `runningTool` is the
 * caller's own reading of `ZeropsThreadModel.running` — this layer no
 * longer reads `lifecycle.recentTools` (§2.1 principle 5: one owner for
 * "which Zerops call is running").
 */
export function buildZeropsServiceMap(
  topology: ZeropsTopologyView | undefined,
  lifecycle?: Known<ZeropsLifecycle>,
  runningTool?: string,
  crewPorts?: ZeropsCrewPortOwners,
  /** The Mate's chats, each with the lease it is bound to: a pool's workspace goes by its chat's title. */
  chats?: ReadonlyArray<LeaseChat>,
): ZeropsServiceMapView | undefined {
  if (topology === undefined) {
    return undefined;
  }

  // A preview folds into its dev row and does not also stand on its own: it holds no checkout
  // — it is where its dev partner's code is deployed, built, unmounted. A part migrated to a dev
  // pool folds its stage under the pool, as zcp's envelope pairs them, or by the pool's stem where
  // the envelope keys none (a pair zcp has not re-keyed, or a lifecycle not read yet). A row folds
  // one stage, so each stage of a pair of several stands on its own rather than be hidden.
  const pools = topology.services
    .filter((entry) => isPoolServiceType(entry.type))
    .map((entry) => entry.hostname);
  const poolPairs = poolPairsOf(lifecycle);
  const { pairs } = servicePairs({ services: topology.services, poolPairs, pools });
  const stageOf = new Map(
    pairs.flatMap(({ dev, previews: [only, ...more] }) =>
      only === undefined || more.length > 0 ? [] : [[dev, only.hostname] as const],
    ),
  );
  const folded = new Set(stageOf.values());
  const leases = lifecycleLeases(lifecycle) ?? [];

  // A part of the app: a dev pool, or a dev service with the stage folded under it. It goes by
  // its real hostname, as every other service does.
  const isPart = (entry: ZeropsTopologyService): boolean =>
    isPoolServiceType(entry.type) || stageOf.has(entry.hostname);
  // A holder: a dev zcp keeps as its repository's stand-in (its envelope's `standIn`) while the
  // project's parts run on their own pools, with no part of its own. Before any pool stands it is
  // still where the code runs.
  const isHolder = (entry: ZeropsTopologyService): boolean =>
    pools.length > 0 &&
    !stageOf.has(entry.hostname) &&
    standInOf(entry.hostname, lifecycle) !== undefined;

  const rowOf = (entry: ZeropsTopologyService): ZeropsServiceRow => {
    const stage = topology.services.find(
      (candidate) => candidate.hostname === stageOf.get(entry.hostname),
    );
    const isPool = isPoolServiceType(entry.type);
    const part = isPart(entry);
    const holder = !part && isHolder(entry);
    const kind: ZeropsServiceRowKind = part ? "part" : holder ? "holder" : "service";
    const crew = crewPorts?.get(entry.hostname);
    // A pool's ports are its slots', balanced over every container: nobody's workspace.
    const portLabel = isPool ? undefined : zeropsPortLabel(entry, crew);
    const workspaces = isPool
      ? poolWorkspaces({ pool: entry.hostname, leases, chats: chats ?? [] })
      : undefined;
    // A pool opens its workspaces' previews; a holder opens nothing, since nothing runs there.
    const routes: ReadonlyArray<ZeropsRouteChoice> =
      workspaces !== undefined
        ? workspaces.flatMap((workspace) =>
            workspace.url === undefined
              ? []
              : [
                  {
                    route: {
                      port: workspace.port ?? 0,
                      url: workspace.url,
                      host: workspace.url.replace(/^https?:\/\//u, "").replace(/\/$/u, ""),
                    },
                    label: workspace.label,
                  },
                ],
          )
        : holder
          ? []
          : zeropsRouteChoices(entry, crew);
    // The control plane is named by its glossary word, its hostname and port a line down; every
    // other service by its hostname.
    const isControlPlane = isZcpServiceType(entry.type);
    const title = isControlPlane ? CONTROL_PLANE_TITLE : entry.hostname;
    const hostnameFact: ReadonlyArray<ZeropsServiceFact> = isControlPlane
      ? [{ id: "hostname", label: `${entry.hostname}${portLabel ?? ""}` }]
      : [];
    return {
      isControlPlane,
      kind,
      ...(holder ? { restNote: HOLDER_NOTE } : {}),
      ...withKey("workspaces", workspaces),
      service: entry,
      tone: holder ? "muted" : serviceStatusTone(entry),
      statusLabel: zeropsStatusWord(entry.status),
      statusNeedsWord: !holder && serviceStatusNeedsWord(entry),
      typeLabel: zeropsTypeLabel(entry.type),
      ...(isControlPlane ? {} : { typeShort: isPool ? POOL_TYPE_SHORT : zeropsTypeShort(entry) }),
      title,
      ...(isControlPlane || portLabel === undefined ? {} : { portLabel }),
      routes,
      routesFolded:
        zeropsRoutesFolded(routes) ||
        (workspaces ?? []).some((workspace) => workspace.url === undefined),
      meta: [...hostnameFact, ...zeropsServiceFacts(entry, title)],
      dashboardUrl: serviceDashboardUrl(entry.serviceId),
      metrics: zeropsServiceMetrics(entry.usage, entry.scaling),
      ...withKey("trends", zeropsUsageTrends(entry.history)),
      ...(stage === undefined
        ? {}
        : {
            stage,
            stageTone: serviceStatusTone(stage),
            stageStatusLabel: zeropsStatusWord(stage.status),
            stageStatusNeedsWord: serviceStatusNeedsWord(stage),
            stageMetrics: zeropsServiceMetrics(stage.usage, stage.scaling),
            ...withKey("stageTrends", zeropsUsageTrends(stage.history)),
            ...withKey("stagePortLabel", zeropsPortLabel(stage)),
            stageRoutes: zeropsRouteChoices(stage),
            stageRoutesFolded: zeropsRoutesFolded(zeropsRouteChoices(stage)),
          }),
      production: productionOf(entry.hostname, lifecycle),
      ...withKey("devServers", devServersOf(entry.hostname, lifecycle)),
    };
  };

  // A dev pool is a runtime kind: it reads with the app's parts, never as infrastructure.
  const groupOf = (entry: ZeropsTopologyService): ZeropsTopologyGroup =>
    isPoolServiceType(entry.type) ? "runtimes" : entry.group;

  const groups = GROUP_ORDER.map(({ group, title }) => ({
    group,
    title,
    rows: topology.services
      .filter((entry) => groupOf(entry) === group && !folded.has(entry.hostname))
      .map(rowOf)
      // Total order within a section: the parts lead Runtimes and a holder rests last;
      // the control plane leads Infrastructure (it is not a service the user created); then
      // hostname, locale-aware/case-insensitive/numeric-aware (`compareZeropsHostnames` — `db` <
      // `db2` < `db10`), tiebroken by `serviceId` so two services can never tie. A folded stage
      // row carries no order of its own — it rides along nested under its dev partner.
      .sort(
        (left, right) =>
          KIND_ORDER[left.kind] - KIND_ORDER[right.kind] ||
          Number(right.isControlPlane) - Number(left.isControlPlane) ||
          compareZeropsHostnames(left.service.hostname, right.service.hostname) ||
          left.service.serviceId.localeCompare(right.service.serviceId),
      ),
  })).filter((group) => group.rows.length > 0);

  return {
    groups,
    project: topology.project,
    isEmpty: topology.services.length === 0,
    warnings: topology.warnings,
    usageRead: topology.usageRead,
    ...(runningTool === undefined ? {} : { runningTool }),
  };
}
